import React, {useState, useRef, useEffect} from 'react'
import { Box, Flex, Text, Button, IconButton, Link, Icon } from '@salesforce/retail-react-app/app/components/shared/ui';
import {FormattedMessage} from 'react-intl';
import {formatDate} from '../../../utils/review-utils';
import {useBreakpointValue} from '@chakra-ui/react'

/* Icons */
import {Recommended, ThumbsUp, ThumbsUpDark, VerifiedBuyerIcon} from '../../icons/index';

/* Components */
import StarRating from './star-rating';
import PhotoSlider from './photo-slider';
import PropTypes from 'prop-types';
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

/* API HOOKS */
import {reviewFeedback} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'

const ReviewCard = ({ review, setSelectedReview, toastContainerId = 'app-main', configs = {}}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [helpfulnessGiven, setHelpfulnessGiven] = useState(false);
    const [inappropriateGiven, setInappropriateGiven] = useState(false);
    const [positiveFeedbackCount, setPositiveFeedbackCount] = useState(review.TotalPositiveFeedbackCount);
    const textRef = useRef(null);
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const defaultConfigs = {
        styles: {
            container: {
                py: 4,
                borderBottom: '1px solid',
                borderBottomColor: 'grey.400',
            },
            nickname: {
                fontWeight: '700',
                fontSize: {base: 'sm'},
                lineHeight: '16px',
                textTransform: {lg: 'uppercase'}
            },
            dimensions: {
                fontSize: {base: 'xs'},
                lineHeight: '15px',
                mt: {base: 2, lg: 1}
            }
        },
        showDimensionStakedOnMobile: false,
        showMobileVersionToDesktop: false,
        showRecommendation: false,
        showImagesOnTopOfReviewText: false,
        showImages: true,
        showFooterActions: true,
        enableReadMore: false
    };

    const mergedConfigs = {
        styles: {
            container: { ...defaultConfigs.styles.container, ...configs?.styles?.container },
            nickname: {...defaultConfigs.styles.nickname, ...configs?.styles?.nickname},
            dimensions: {...defaultConfigs.styles.dimensions, ...configs?.styles?.dimensions}
        },
        showDimensionStakedOnMobile : configs?.showDimensionStakedOnMobile ?? defaultConfigs.showDimensionStakedOnMobile,
        showMobileVersionToDesktop: configs?.showMobileVersionToDesktop ?? defaultConfigs.showMobileVersionToDesktop,
        showRecommendation: configs?.showRecommendation ?? defaultConfigs.showRecommendation,
        showImagesOnTopOfReviewText: configs?.showImagesOnTopOfReviewText ?? defaultConfigs.showImagesOnTopOfReviewText,
        showFooterActions: configs?.showFooterActions ?? defaultConfigs.showFooterActions,
        enableReadMore: configs?.enableReadMore ?? defaultConfigs.enableReadMore,
        showImages: configs?.showImages ?? defaultConfigs.showImages
    };

    const {submitFeedback} = reviewFeedback();

    const contextValues = review?.ContextDataValues || {};
    const isVerifiedPurchase = (contextValues.isReviewFromVerifiedPurchase?.Value || '').toString().toLowerCase() === 'yes';
    const isIncentivized = (contextValues.isReviewIncentivized?.Value || '').toString().toLowerCase() === 'yes';

    /* Hooks */
    useEffect(() => {
        if (textRef.current) {
            setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight);
        }

        // Retrieve feedback data from localStorage
        if (localStorage.getItem('review-feedback')) {
            const feedbackData = JSON.parse(localStorage.getItem('review-feedback')) || {};
            const reviewFeedback = feedbackData[review.Id];

            if (reviewFeedback) {
                setHelpfulnessGiven(reviewFeedback.helpfulnessGiven || false);
                setInappropriateGiven(reviewFeedback.inappropriateGiven || false);
                setPositiveFeedbackCount(reviewFeedback.positiveCount || review.TotalPositiveFeedbackCount);
            } else {
                setHelpfulnessGiven(false);
                setInappropriateGiven(false);
                setPositiveFeedbackCount(review?.TotalPositiveFeedbackCount);
            }
        }
    }, [review?.ReviewText]);

    /* Event handlers */
     const handleFeedback = async (Id, type) => {
        let feedbackData = {};

        if (localStorage.getItem('review-feedback')) {
            feedbackData = JSON.parse(localStorage.getItem('review-feedback'));
        }

        const reviewFeedback = feedbackData[Id] || { positiveCount: review.TotalPositiveFeedbackCount, negativeCount: review.TotalInappropriateFeedbackCount };

        if (type === 'helpfulness' && !helpfulnessGiven) {
            const result = await submitFeedback({ Id, feedback: type });

            if (result.hasError) {
                return;
            }

            const newCount = reviewFeedback.positiveCount + 1;
            setPositiveFeedbackCount(newCount);
            reviewFeedback.positiveCount = newCount;
            reviewFeedback.helpfulnessGiven = true;
            setHelpfulnessGiven(true);
        } else if (type === 'inappropriate' && !inappropriateGiven) {
            const result = await submitFeedback({ Id, feedback: type });

            if (result.hasError) {
                return;
            }

            reviewFeedback.inappropriateGiven = true;
            setInappropriateGiven(true);
        }

        feedbackData[Id] = reviewFeedback;
        localStorage.setItem('review-feedback', JSON.stringify(feedbackData));
    };

    const toggleReadMore = () => {
        setIsExpanded(!isExpanded);
    };

    /* Template */
    return (
        <Box {...mergedConfigs.styles.container}>
            <Flex justifyContent="space-between"
                mb={{base: 3, lg: mergedConfigs.showMobileVersionToDesktop ? 3 : 2}}
                lineHeight={{base: '15px'}}>
                <HideOnDesktop display={{base:'block', lg: mergedConfigs.showMobileVersionToDesktop ? 'block' : 'none'}}>
                    <Box lineHeight={{base: '12px'}}>
                        <StarRating
                            key={review?.Id}
                            initialRating={Number(review?.Rating)}
                            lock={true} styles={{starWidth: '12',
                            starHeight: '12'}} />
                    </Box>
                </HideOnDesktop>
                <HideOnMobile display={{base:'none', lg: mergedConfigs.showMobileVersionToDesktop ? 'none' : 'block'}}>
                    <Text {...mergedConfigs.styles.nickname}>
                        {review?.UserNickname}
                    </Text>
                    {isVerifiedPurchase && (
                        <Flex alignItems="center" mt={{base: 2, lg: 1}} gap={1.5}>
                            <Icon as={VerifiedBuyerIcon} color="green.500" boxSize={4} flexShrink={0} />
                            <Text fontSize="xs" lineHeight="16px" fontWeight="400" color="black">
                                <FormattedMessage defaultMessage="Verified Buyer" id="pdp_reviews.badge.verified_buyer" />
                            </Text>
                        </Flex>
                    )}
                </HideOnMobile>
                <Box>
                    <Text fontSize={{base: 'xs'}}
                        lineHeight={{base: '15px'}}
                        color={{base: 'warmBlack', lg: mergedConfigs.showMobileVersionToDesktop ? 'warmBlack' : 'black'}}>
                        {formatDate(review?.SubmissionTime)}
                    </Text>
                </Box>
            </Flex>

            <Flex flexDirection={{base: 'column', lg: mergedConfigs.showMobileVersionToDesktop ? 'column' : 'row'}}
                gap={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : '100px'}}>
                <Box mb={{base: 4, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 0}}
                    mt={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : 3}}
                    flexBasis={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : '250px'}}
                    flexShrink={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : '0'}}>
                    <HideOnDesktop display={{base:'block', lg: mergedConfigs.showMobileVersionToDesktop ? 'block' : 'none'}}>
                        <Text fontWeight={500}
                            fontSize={{base: 'md'}}
                            lineHeight="100%"
                            mb={{base: 4}}>
                            {review?.Title}
                        </Text>
                        <Text {...mergedConfigs.styles.nickname}>
                            {review?.UserNickname}
                        </Text>
                        {isVerifiedPurchase && (
                            <Flex alignItems="center" mt={{base: 2, lg: 1}} gap={1.5}>
                                <Icon as={VerifiedBuyerIcon} color="green.500" boxSize={4} flexShrink={0} />
                                <Text fontSize="xs" lineHeight="16px" fontWeight="400" color="black">
                                    <FormattedMessage defaultMessage="Verified Buyer" id="pdp_reviews.badge.verified_buyer" />
                                </Text>
                            </Flex>
                        )}
                    </HideOnDesktop>

                    {/* Dimensions */}
                    <Flex flexDirection={{base: mergedConfigs.showDimensionStakedOnMobile ? 'column' : '', lg: 'column'}}
                        flexWrap={{base: 'wrap'}}>
                        {Object.keys(review?.ContextDataValues || {}).map((key, index) => {
                            const contextData = review?.ContextDataValues[key];

                            const keyLowerCase = key.toLowerCase();
                            if (keyLowerCase == "height" || keyLowerCase == "weight") {
                                return (<Text key={'dim-' + index} as={'span'}
                                    {...mergedConfigs.styles.dimensions}>

                                    {contextData?.DimensionLabel} : {contextData?.ValueLabel} {(!mergedConfigs.showDimensionStakedOnMobile && !isDesktop) && (<Text as={'span'} mx={{base: 1}}>/</Text>)}
                                </Text>)
                            }
                        })}

                        {Object.keys(review?.AdditionalFields).map((key, index) => {
                            const additionalFields = review?.AdditionalFields[key];

                            return (<Text key={'add-' + index} as={'span'}
                                {...mergedConfigs.styles.dimensions}>
                                {additionalFields?.Label} : {additionalFields?.Value} {(!mergedConfigs.showDimensionStakedOnMobile && !isDesktop) && (<Text as={'span'} mx={{base: 1}}>/</Text>)}
                            </Text>)
                        })}

                        {review?.SecondaryRatings?.HowDoesItFit?.ValueLabel && (
                            <Text as={'span'}
                                {...mergedConfigs.styles.dimensions}>
                                <FormattedMessage
                                    defaultMessage="Fit"
                                    id="pdp_reviews.description.fit"
                                /> : {review?.SecondaryRatings?.HowDoesItFit?.ValueLabel}
                            </Text>
                        )}
                        <HideOnMobile display={{base:'none', lg: mergedConfigs.showMobileVersionToDesktop ? 'none' : 'block'}}>
                            <Box lineHeight={{base: '16px'}}
                                mt={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : 6}}>
                                <StarRating
                                    key={review?.Id}
                                    initialRating={Number(review?.Rating)}
                                    lock={true} styles={{starWidth: '16',
                                    starHeight: '16'}} />
                            </Box>
                        </HideOnMobile>
                    </Flex>
                    {review?.IsRecommended && mergedConfigs.showRecommendation && (
                        <Flex alignItems={'center'}
                            mt={{base: 4, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 6}}>
                            <Recommended></Recommended>
                            <Text
                                fontSize={{base: 'xs'}}
                                lineHeight="15px"
                                ml={{base: 2}}>
                                <FormattedMessage
                                    defaultMessage="Recommended"
                                    id="pdp_reviews.description.recommended"
                                />
                            </Text>
                        </Flex>
                    )}
                </Box>
                <Flex flexBasis={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : '100%'}}
                    flexDirection={{base: 'column'}}
                    justifyContent={{base: 'space-between'}}>
                    <Box>
                        <HideOnMobile display={{base:'none', lg: mergedConfigs.showMobileVersionToDesktop ? 'none' : 'block'}}>
                            <Text fontWeight={500}
                                fontSize={{base: 'md'}}
                                lineHeight="100%"
                                mb={{base: 2}}>
                                {review?.Title}
                            </Text>
                        </HideOnMobile>
                        {mergedConfigs.showImages && mergedConfigs.showImagesOnTopOfReviewText && review?.Photos.length > 0 && (
                            <Box mb={{base: 4, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 6}}>
                                <PhotoSlider review={review} setSelectedReview={setSelectedReview} />
                            </Box>
                        )}

                        <Text ref={textRef}
                            fontSize={{base: 'sm'}}
                            lineHeight="18px"
                            as="p"
                            sx={mergedConfigs.enableReadMore && {
                                display: '-webkit-box',
                                WebkitLineClamp: isExpanded ? 'none' : 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                            {isIncentivized && '[This reviewer received Loyalty points in exchange for an honest review] '}
                            {review?.ReviewText}
                        </Text>
                        {mergedConfigs.enableReadMore && isOverflowing  && !isExpanded &&
                            <Button variant={'link'}
                                fontSize={{base: 'xs'}}
                                letterSpacing={{base: '1%'}}
                                lineHeight={{base: '15px'}}
                                onClick={toggleReadMore}
                                mt={{base: 4}}>
                                <FormattedMessage
                                    defaultMessage="Read More"
                                    id="pdp_reviews.text.read.more.link"
                                />
                            </Button>
                        }
                        {mergedConfigs.showImages && !mergedConfigs.showImagesOnTopOfReviewText && review?.Photos.length > 0 && (
                            <Box mt={{base: 4, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 6}}>
                                <PhotoSlider review={review} setSelectedReview={setSelectedReview}/>
                            </Box>
                        )}
                    </Box>
                    <Flex justifyContent={{base: 'space-between'}}
                        alignItems={{base: 'center'}}
                        mt={{base: 4, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 6}}
                        display={mergedConfigs.showFooterActions ? 'flex' : 'none'}>
                        <Flex  alignItems={{base: 'center'}}
                            lineHeight="15px">
                            <Text fontSize={{base: 'xs'}}
                                lineHeight="15px"
                                mr={{base: 8}}>
                                <FormattedMessage
                                    defaultMessage="Was this helpful?"
                                    id="pdp_reviews.footer.helpful"
                                />
                            </Text>
                            <Flex alignItems={{base: 'center'}}>
                                {helpfulnessGiven &&
                                    <IconButton
                                        as={Link}
                                        href='#'
                                        variant="unstyled"
                                        h={'auto'}
                                        minW={'auto'}
                                        cursor={'unset'}
                                        onClick={(e) => {
                                            if (e) e.preventDefault()
                                            handleFeedback(review?.Id, 'helpfulness')
                                        }}
                                        icon={<ThumbsUpDark width={{base: '20px'}} height={{base: '20px'}} />}
                                        aria-label="Like report"
                                    />
                                }
                                {!helpfulnessGiven &&
                                    <IconButton
                                        as={Link}
                                        href='#'
                                        variant="unstyled"
                                        h={'auto'}
                                        minW={'auto'}
                                        onClick={(e) => {
                                            if(e) e.preventDefault()
                                            handleFeedback(review?.Id, 'helpfulness')
                                        }}
                                        icon={<ThumbsUp width={{base: '20px'}} height={{base: '20px'}} />}
                                        aria-label="Unlike report"
                                    />
                                }
                                <Text fontSize={{base: 'xs'}}
                                    lineHeight="15px"
                                    ml={{base: 2}}>
                                    ({positiveFeedbackCount})
                                </Text>
                            </Flex>
                        </Flex>
                        <Box lineHeight="15px">
                            <Button
                                as={Link}
                                href='#'
                                variant="link"
                                aria-label="Report the review"
                                fontSize={{base: 'xs'}}
                                lineHeight="15px"
                                letterSpacing={'1%'}
                                textDecoration={inappropriateGiven ? 'none' : 'underline'}
                                _hover={{
                                    textDecoration: inappropriateGiven ? 'none' : 'underline'
                                }}
                                cursor={inappropriateGiven ? 'default' : 'pointer'}
                                onClick={(e) => {
                                    if (e) e.preventDefault()
                                    handleFeedback(review?.Id, 'inappropriate')
                                }}>
                                {inappropriateGiven ? (
                                    <FormattedMessage
                                        defaultMessage="Reported"
                                        id="pdp_reviews.footer.reported"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Report"
                                        id="pdp_reviews.footer.report"
                                    />
                                )}
                            </Button>
                        </Box>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};

ReviewCard.propTypes = {
    review: PropTypes.object,
    setSelectedReview: PropTypes.func,
    configs: PropTypes.object,
    containerId: PropTypes.string
};

export default ReviewCard;
